import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSignIn, useClerk } from '@clerk/clerk-react';

import { Loader } from '@/components/ui/loader';

/**
 * Consumes a Clerk `ticket` query param and signs the user in as the target
 * organisation. Used by HQ's "view as retailer" flow and the local
 * `yarn dev` impersonation script. We use `window.location.href` after
 * `setActive` to dodge a race with Clerk's React context update.
 */
export function Impersonate() {
	const [queryParams] = useSearchParams();
	const signInToken = queryParams.get('ticket');
	const shouldResetAssistant = queryParams.get('reset_assistant') === '1';
	const { signIn, setActive } = useSignIn();
	const clerk = useClerk();
	const hasRun = useRef(false);

	if (!signInToken) throw new Error('No token provided!');

	useEffect(() => {
		const run = async () => {
			if (!signIn || hasRun.current) return;
			hasRun.current = true;

			if (shouldResetAssistant) {
				try {
					for (let i = localStorage.length - 1; i >= 0; i--) {
						const key = localStorage.key(i);
						if (key && key.startsWith('unify_assistant_session_')) {
							localStorage.removeItem(key);
						}
					}
				} catch (err) {
					console.warn('Failed to reset assistant history:', err);
				}
			}

			try {
				// A pre-existing session makes `signIn.create` throw "session already
				// exists" (→ dead-ends at /login). `clerk.signOut()` navigates away by
				// default, so we can't sign out inline then create. Instead sign out
				// with a redirect back to THIS ticket URL — the sessionless remount then
				// consumes the ticket cleanly. Common in dev/QA where the same browser
				// (or incognito profile) still holds an earlier session.
				if (clerk?.session) {
					await clerk.signOut({ redirectUrl: window.location.href });
					return;
				}
				const res = await signIn.create({ strategy: 'ticket', ticket: signInToken });
				await setActive({ session: res.createdSessionId });
				window.location.href = '/';
			} catch (error) {
				console.error('Impersonation failed:', error);
				window.location.href = '/login';
			}
		};

		run();
	}, [signIn, signInToken, shouldResetAssistant, setActive, clerk]);

	return <Loader label="Signing you in…" />;
}
